import {
  BUILT_IN_TOOLS,
  LANGUAGE_NAMES,
  LANGUAGES,
  LENGTHS,
  STARTING_SETTINGS,
  TONES,
  type AiTool,
  type BuiltInTool,
  type CustomTone,
  type Language,
  type Length,
  type Menu,
  type MenuSetting,
  type SavedInstruction,
  type Tone,
} from '@lyrmo/shared';
import type { IconName } from '../../components/Icon';
import type { Messages } from '../../i18n/en';

/**
 * A choice in one of the writer's menus, AI tools, tones, or saved instructions, as the menu and
 * the dialog managing them show it: built-in, or one of the writer's own.
 */
export interface MenuItem {
  /** A built-in choice's name, such as `bullets`, or the id of one of the writer's own. */
  readonly id: string;
  readonly name: string;
  /** What it does, in a line. */
  readonly hint: string;
  readonly icon: IconName;
  /** A short code shown in place of the icon, such as a language's. */
  readonly badge?: string;
  readonly builtIn: boolean;
  /** Left out of the menu, though still listed where it's managed. */
  readonly hidden: boolean;
  /** Listed first in the menu. */
  readonly pinned: boolean;
}

/** A choice before the writer's settings say how it's kept. */
type Choice = Omit<MenuItem, 'hidden' | 'pinned'>;

const TOOL_ICONS: Readonly<Record<BuiltInTool, IconName>> = {
  bullets: 'bullets',
  numbered: 'numbered',
  checklist: 'checklist',
  gherkin: 'gherkin',
  summary: 'summary',
  'line-breaks': 'wrap',
  simplify: 'simplify',
  plain: 'plain-text',
};

const TONE_ICONS: Readonly<Record<Tone, IconName>> = {
  natural: 'quote',
  friendly: 'smile',
  casual: 'coffee',
  formal: 'briefcase',
  confident: 'bolt',
  empathetic: 'heart',
  persuasive: 'megaphone',
  diplomatic: 'scale',
  direct: 'arrow',
  polite: 'flower',
  enthusiastic: 'star',
  inspiring: 'sun',
  playful: 'wink',
  academic: 'cap',
  objective: 'ruler',
};

// The writer's own all look alike in each menu, as there's no icon to choose.
export const OWN_TOOL_ICON: IconName = 'wand';
export const OWN_TONE_ICON: IconName = 'message';
export const INSTRUCTION_ICON: IconName = 'sparkle';

const UNCHANGED = { hidden: false, pinned: false } as const;

const LENGTH_ICONS: Readonly<Record<Length, IconName>> = {
  same: 'length-same',
  shorter: 'length-shorter',
  longer: 'length-longer',
};

/** The lengths a rewrite can take, as their menu lists them: the default first. */
export function lengthItems(t: Messages): readonly (MenuItem & { readonly id: Length })[] {
  return LENGTHS.map((id) => ({
    id,
    name: t.options.lengths[id],
    hint: t.options.lengthHints[id],
    icon: LENGTH_ICONS[id],
    builtIn: true,
    ...UNCHANGED,
  }));
}

const autonyms = new Map<Language, string>();

/** What a language calls itself, such as Deutsch: what its own writers would look for. */
function autonym(language: Exclude<Language, 'auto'>): string {
  const known = autonyms.get(language);
  if (known) return known;
  const tag = language === 'zh' ? 'zh-Hans' : language;
  let name: string = LANGUAGE_NAMES[language];
  try {
    const own = new Intl.DisplayNames([tag], { type: 'language' }).of(tag);
    if (own) name = own.charAt(0).toLocaleUpperCase(tag) + own.slice(1);
  } catch {
    // Without the language's own data, its English name will do.
  }
  autonyms.set(language, name);
  return name;
}

/**
 * The languages a rewrite can be written in, as their menu lists them: "Same as my text", the
 * default, first; then each named in the interface's language, with its own name under it and
 * its code in place of an icon, so it's found either way.
 */
export function languageItems(
  name: (language: Language) => string,
  t: Messages,
  pinned: readonly Language[] = [],
): readonly (MenuItem & { readonly id: Language })[] {
  return LANGUAGES.map((id) =>
    id === 'auto'
      ? {
          id,
          name: name(id),
          hint: t.options.sameLanguageHint,
          icon: 'globe',
          builtIn: true,
          ...UNCHANGED,
        }
      : {
          id,
          name: name(id),
          hint: autonym(id),
          icon: 'globe',
          badge: id.toUpperCase(),
          builtIn: true,
          ...UNCHANGED,
          pinned: pinned.includes(id),
        },
  );
}

/** `choices` as the writer keeps them, or as everyone starts until that's known. */
function kept(
  menu: Menu,
  choices: readonly Choice[],
  settings: readonly MenuSetting[] | null | undefined,
) {
  const byId = new Map(
    (settings ?? STARTING_SETTINGS[menu]).map((setting) => [setting.id, setting]),
  );
  return choices.map((choice): MenuItem => {
    const { hidden, pinned } = byId.get(choice.id) ?? UNCHANGED;
    return { ...choice, hidden, pinned };
  });
}

/** Every AI tool the writer has, the built-in ones first. */
export function toolItems(
  own: readonly AiTool[],
  settings: readonly MenuSetting[] | null | undefined,
  t: Messages,
): readonly MenuItem[] {
  return kept(
    'tools',
    [
      ...BUILT_IN_TOOLS.map((id) => ({
        id,
        ...t.aiTools.tools[id],
        icon: TOOL_ICONS[id],
        builtIn: true,
      })),
      ...own.map(({ id, name, instruction }) => ({
        id,
        name,
        hint: instruction,
        icon: OWN_TOOL_ICON,
        builtIn: false,
      })),
    ],
    settings,
  );
}

/** One line on how a tone of the writer's sounds: its description, or else how many rules. */
export function toneSummary({ description, rules }: CustomTone, t: Messages): string {
  if (description) return description;
  const count = rules.terms.length + rules.avoid.length + (rules.notes ? 1 : 0);
  return count > 0 ? t.tones.rules(count) : t.tones.noRules;
}

/** Every tone the writer has, the built-in ones first. */
export function toneItems(
  own: readonly CustomTone[],
  settings: readonly MenuSetting[] | null | undefined,
  t: Messages,
): readonly MenuItem[] {
  return kept(
    'tones',
    [
      ...TONES.map((id) => ({
        id,
        name: t.options.tones[id],
        hint: t.tones.about[id],
        icon: TONE_ICONS[id],
        builtIn: true,
      })),
      ...own.map((tone) => ({
        id: tone.id,
        name: tone.name,
        hint: toneSummary(tone, t),
        icon: OWN_TONE_ICON,
        builtIn: false,
      })),
    ],
    settings,
  );
}

/** Every instruction the writer saved: there are no built-in ones. */
export function instructionItems(
  own: readonly SavedInstruction[],
  settings: readonly MenuSetting[] | null | undefined,
): readonly MenuItem[] {
  return kept(
    'instructions',
    own.map(({ id, name, text }) => ({
      id,
      name,
      hint: text,
      icon: INSTRUCTION_ICON,
      builtIn: false,
    })),
    settings,
  );
}

/** Whether a choice's name or what it does has `query` in it, ignoring case. */
export function matches(item: MenuItem, query: string): boolean {
  const wanted = query.trim().toLocaleLowerCase();
  return (
    !wanted ||
    item.name.toLocaleLowerCase().includes(wanted) ||
    item.hint.toLocaleLowerCase().includes(wanted)
  );
}

/**
 * A menu's sections: the pinned choices, then the rest of the built-in ones, then the rest of
 * the writer's own, leaving out hidden ones unless `keep` keeps them, such as the one chosen.
 */
export function menuSections(
  items: readonly MenuItem[],
  keep: (item: MenuItem) => boolean = () => false,
) {
  const shown = items.filter((item) => !item.hidden || keep(item));
  return {
    pinned: shown.filter((item) => item.pinned),
    builtIn: shown.filter((item) => item.builtIn && !item.pinned),
    own: shown.filter((item) => !item.builtIn && !item.pinned),
    hidden: items.length - shown.length,
  };
}

/** `settings` with one choice's changed. Back as every choice starts, it isn't listed. */
export function withSetting(settings: readonly MenuSetting[], setting: MenuSetting): MenuSetting[] {
  const others = settings.filter((kept) => kept.id !== setting.id);
  return setting.hidden || setting.pinned ? [...others, setting] : others;
}
